import { Box, Typography, useTheme } from "@mui/material";
import { t, Trans } from "@lingui/macro";
import { UserType } from "@WahooFitness/cloud-client-types";
import CustomStepper from "./CustomStepper";
import useAthleteOnboarding from "./useAthleteOnboarding";
import AthleteOnboardingFeatureScreen from "./AppFeatureScreen";
import { WfButton, WfIconNavigationChevronLeftRegular } from "@WahooFitness/redesignr";
import WfActionGroup from "@WahooFitness/redesignr/dist/Actions/WfActionGroup/WfActionGroup.js";
import { GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";

const AthleteOnboarding = ({ user }: { user: UserType }) => {
  const {
    activeStep,
    steps,
    handleNext,
    handleBack,
    handleSkip,
    showFeatureScreen,
    selectedSportOptions,
  } = useAthleteOnboarding(user);
  const { palette } = useTheme();

  if (showFeatureScreen) {
    return (
      <>
        <GlobalHeader
          primaryLeftActionItem={{
            icon: <WfIconNavigationChevronLeftRegular />,
            action: handleBack,
            variant: "onMap",
          }}
          isTransparent
        />
        <AthleteOnboardingFeatureScreen
          selectedSportOptions={selectedSportOptions}
          handleNext={handleNext}
        />
      </>
    );
  }

  return (
    // the use of a boxShadow here is a way to extend the background color
    // of the page beyond the maxWidth of the component to cover the whole screen
    <>
      <GlobalHeader
        primaryLeftActionItem={{
          icon: <WfIconNavigationChevronLeftRegular />,
          action: handleBack,
          disabled: activeStep === 0,
        }}
        primaryRightActionItem={
          steps[activeStep].skippable
            ? {
                text: t`Skip`,
                action: handleSkip,
              }
            : undefined
        }
      />
      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        sx={{ boxShadow: `0 0 0 100vmax ${palette.background.level1}` }}
      >
        <Box
          display="flex"
          position="absolute"
          left={0}
          pl={2}
          py={1}
          sx={{ backgroundColor: palette.background.level1 }}
        >
          <CustomStepper steps={steps} activeStep={activeStep} />
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          flexGrow={1}
          sx={{ backgroundColor: palette.background.level1 }}
        >
          <Box display="flex" flexDirection="column" px={2} pt={[5, 17.5]} rowGap={1.5}>
            <Typography variant="ui-4xl-bold">{steps[activeStep].headline}</Typography>
            <Typography variant="prose-base" color="text.secondary">
              {steps[activeStep].subheader}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" pt={[4.5, 9]} width="100%" px={2}>
            {steps[activeStep].component}
          </Box>
        </Box>

        <WfActionGroup orientation="vertical" fullWidth sticky>
          <WfButton
            hierarchy="primary"
            size="large"
            fullWidth
            onClick={handleNext}
            disabled={steps[activeStep].disabled}
          >
            <Trans>Continue</Trans>
          </WfButton>
        </WfActionGroup>
      </Box>
    </>
  );
};

export default AthleteOnboarding;
